import { Injectable, inject } from '@angular/core';
import { ChatPersistenceService } from '../../../core/services/chat-persistence.service';
import { LmStudioService } from '../../../core/services/lm-studio.service';

export type QueryIntent =
  | 'general'
  | 'research'
  | 'technical'
  | 'coding'
  | 'document_question'
  | 'comparison'
  | 'troubleshooting'
  | 'other';

export interface QueryAnalysis {
  intent: QueryIntent;
  topics: string[];
  entities: string[];
  freshnessRequired: boolean;
  useWeb: boolean;
  useRag: boolean;
  searchQueries: string[];
  ragQueries: string[];
}

export interface RetrievalResult {
  // Deliberately generic: future filtering/reranking must not couple this service to a retrieval provider.
  [key: string]: unknown;
}

/** Contract deliberately leaves retrieval post-processing opt-in until it is implemented. */
export interface HelperModelApi {
  analyzeQuery(query: string): Promise<QueryAnalysis>;
  rerankResults?(query: string, results: RetrievalResult[]): Promise<RetrievalResult[]>;
  compressContext?(query: string, results: RetrievalResult[]): Promise<string>;
}

const QUERY_INTENTS: QueryIntent[] = ['general', 'research', 'technical', 'coding', 'document_question', 'comparison', 'troubleshooting', 'other'];

const QUERY_ANALYSIS_PROMPT = `You are a query-processing assistant. Your role is NOT to answer the user's question. Analyze the user's request and prepare it for another AI model.

Determine the user's intent, topics, important entities, whether fresh information is required, whether web search or internal document retrieval would help, and concise search/retrieval queries. Do not solve the request.

Return only valid JSON matching exactly this schema:
{"intent":"general|research|technical|coding|document_question|comparison|troubleshooting|other","topics":["string"],"entities":["string"],"freshnessRequired":true,"useWeb":true,"useRag":false,"searchQueries":["string"],"ragQueries":["string"]}

Search queries must be concise, preserve important terminology, avoid filler, and only be multiple when they cover different aspects.`;

@Injectable({ providedIn: 'root' })
export class HelperModelService implements HelperModelApi {
  private readonly llm = inject(LmStudioService);
  private readonly persistence = inject(ChatPersistenceService);

  isEnabled(): boolean {
    return this.llm.helperModelId() !== undefined;
  }

  async analyzeQuery(query: string): Promise<QueryAnalysis> {
    const helper = this.persistence.getEffectiveSettings().helperModel;
    const modelId = helper.modelId?.trim() || this.llm.mainModelId();
    if (!helper.enabled) {
      throw new Error('Helper model is not configured.');
    }

    console.debug('[HelperModel] Enabled', { model: modelId });
    const answer = await this.llm.chat(
      [{ author: 'user', text: query, time: '' }],
      { systemPrompt: QUERY_ANALYSIS_PROMPT, model: modelId, maxTokens: 700, reasoningEffort: 'low', timeoutMs: 8_000, useHelperApi: true },
    );
    const analysis = parseQueryAnalysis(answer);
    console.debug('[HelperModel] Analysis', {
      model: modelId,
      intent: analysis.intent,
      web: analysis.useWeb,
      rag: analysis.useRag,
      webQueries: analysis.searchQueries.length,
      ragQueries: analysis.ragQueries.length,
    });
    return analysis;
  }

}

/** Parses and validates the strict JSON contract returned by the helper model. */
export function parseQueryAnalysis(answer: string): QueryAnalysis {
  const json = answer.match(/\{[\s\S]*\}/)?.[0];
  if (!json) throw new Error('Helper model did not return JSON.');
  const value = JSON.parse(json) as Partial<QueryAnalysis>;
  if (!QUERY_INTENTS.includes(value.intent as QueryIntent)
    || !isStringArray(value.topics)
    || !isStringArray(value.entities)
    || typeof value.freshnessRequired !== 'boolean'
    || typeof value.useWeb !== 'boolean'
    || typeof value.useRag !== 'boolean'
    || !isStringArray(value.searchQueries)
    || !isStringArray(value.ragQueries)) {
    throw new Error('Helper model returned an invalid query analysis.');
  }
  return {
    intent: value.intent as QueryIntent,
    topics: value.topics,
    entities: value.entities,
    freshnessRequired: value.freshnessRequired,
    useWeb: value.useWeb,
    useRag: value.useRag,
    searchQueries: value.searchQueries,
    ragQueries: value.ragQueries,
  };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}
